import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePrevious } from 'react-use';

import { createStyles, Typography, withStyles, WithStyles } from '@material-ui/core';
import { AxiosRequestConfig } from 'axios';
import { Button, MessageTemplate } from 'core';
import ContentTableLoader from 'core/AvailabilitySelector/ContentTableLoader';
import { TDateInterval } from 'core/AvailabilitySelector/useAvailabilitiesInitialDatesValue';
import { concat, filter, flatMap, get, isEmpty, omit, pick, pickBy } from 'lodash';
import { Moment } from 'moment';
import { axiosService, TAvailability } from 'ui-model';
import { createKeys, InfiniteQueryOptions, useCreateInfiniteQuery } from 'ui-model/dist/CRUD';

import { showMessage } from '@lib/actions';
import { selectUserSettings } from '@lib/selectors';

import ListAvailabilitiesGrouped, { ListAvailabilitiesGroupedProps } from './ListAvailabilitiesGrouped';

const styles = () =>
  createStyles({
    loadMoreWrapper: {
      width: '100%',
      textAlign: 'center',
    },
  });

const keys = createKeys('availabilities');

const PAGE_LIMIT = 100;
const SCHEDULER_BASE_PARAMS = {
  limit: PAGE_LIMIT,
  sort: 'date',
  sortBy: 'asc',
  status: 'available',
  populate: true,
  includeNullApptTypes: true,
};

type TData = {
  response: TAvailability[];
  appointmentTypes: string[];
  facilities: string[];
  providers: string[];
  page: number;
  size: number;
};

export type TDistance = {
  zipcode: string;
  zipcodeMaxDistance: number;
};

export type TFilters = {
  facilities: string[];
  providers: string[];
  distance: TDistance;
  appointmentTypes: string[];
};

// TODO: Move to `ui-model` once it's validated by QA in prod
// TODO: useCreateInfiniteQuery is not returning isRefetching causing loading component to do not be displayed during a refetch
const useAvailabilitySchedulerPagination = (
  initialParams?: AxiosRequestConfig['params'],
  options?: InfiniteQueryOptions<TData>,
) =>
  useCreateInfiniteQuery(
    keys.many(initialParams),
    async (allParams) => {
      const params = get(allParams, 'zipcode', null) ? allParams : omit(allParams, 'zipcodeMaxDistance');
      const { data } = await axiosService.get('/v2/availabilities/scheduler', {
        params,
      });
      return data as TData;
    },
    {
      ...SCHEDULER_BASE_PARAMS,
      ...pickBy(initialParams, (param) => param),
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.response.length < PAGE_LIMIT) return undefined;
        return allPages.length + 1;
      },
      // Avoid calls missing the date range param
      enabled: filter(initialParams?.date, Boolean)?.length === 2,
      // @ts-ignore TODO: Expose option from `ui-model#InfiniteQueryOptions` type
      refetchOnWindowFocus: false,
      retry: 3,
      ...options,
    },
  );

export interface Props extends Omit<ListAvailabilitiesGroupedProps, 'availabilities'>, WithStyles<typeof styles> {
  datesRange?: TDateInterval | null;
  datesValue?: TDateInterval;
  hasAccessSchedulerEnhancements?: boolean;
  isMobile?: boolean;
  onChange: (availability: TAvailability) => void;
  onDatesChange?: (value: TDateInterval) => void;
  onFiltersReceived?: (filters: Partial<TFilters>) => void;
  onRecursiveLoadingEnd?: () => void;
  onRecursiveLoadingStart?: () => void;
  params: any;
  showFilters?: boolean;
}

const MAX_DAYS_TO_QUERY_RTA_DEFAULT = 30;

const QueryListAvailabilitiesGrouped: React.FC<Props> = ({
  classes,
  datesRange,
  datesValue,
  hasAccessSchedulerEnhancements = false,
  isMobile,
  onChange,
  onDatesChange,
  onFiltersReceived,
  onRecursiveLoadingEnd,
  onRecursiveLoadingStart,
  params,
  showFilters,
  ...rest
}) => {
  const settings = useSelector(selectUserSettings);
  const dispatch = useDispatch();
  const maxDaysToQueryRTAPerRequest = get(
    settings,
    'scheduler.maxDaysToQueryRTAPerRequest',
    MAX_DAYS_TO_QUERY_RTA_DEFAULT,
  );
  const [availabilities, setAvailabilities] = React.useState<any>(null);
  const [isRecursiveInitialLoading, setIsRecursiveInitialLoading] = React.useState(
    !!(
      availabilities === null &&
      hasAccessSchedulerEnhancements &&
      datesRange?.startDate &&
      datesRange?.endDate &&
      datesRange.endDate.diff(datesRange.startDate, 'days') > maxDaysToQueryRTAPerRequest
    ),
  );
  const prevIsRecursiveInitialLoading = usePrevious(isRecursiveInitialLoading);

  const { isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, isError } = useAvailabilitySchedulerPagination(
    params,
    {
      onSuccess: (data) => {
        if (onFiltersReceived) {
          onFiltersReceived(pick(data.pages[0], ['appointmentTypes', 'facilities', 'providers']));
        }
        const allAvailabilities = flatMap(data?.pages, 'response');

        setAvailabilities(
          isRecursiveInitialLoading ? concat(availabilities || [], allAvailabilities) : allAvailabilities,
        );
        if (isRecursiveInitialLoading && datesValue && datesRange) {
          if (data.pages[0]?.response?.length) {
            // stop calling BE when find any availability
            setIsRecursiveInitialLoading(false);
          } else if (datesValue.endDate && datesValue.endDate.diff(datesRange.endDate, 'days') >= 0) {
            // stop calling BE when date interval reaches maxDaysToShow date
            setIsRecursiveInitialLoading(false);
          } else {
            const newStartDate = (datesValue.endDate as Moment).clone();
            const newEndDate = newStartDate.clone();

            newEndDate.add(maxDaysToQueryRTAPerRequest, 'days');

            if (onDatesChange) {
              onDatesChange({
                startDate:
                  (newStartDate && newStartDate.diff(datesRange.endDate, 'days') > 0
                    ? datesRange.endDate
                    : newStartDate) || null,
                endDate:
                  (newEndDate && newEndDate.diff(datesRange.endDate, 'days') > 0 ? datesRange.endDate : newEndDate) ||
                  null,
              });
            }
          }
        }
      },
      onError: (error) => {
        const statusText = get(error, 'response.data.statusText', '');
        if (statusText) {
          dispatch(showMessage(statusText, 'error'));
        }
      },
    },
  );

  React.useEffect(() => {
    if (isError && isRecursiveInitialLoading && onRecursiveLoadingEnd) {
      onRecursiveLoadingEnd();
    }
  }, [isError, isRecursiveInitialLoading, onRecursiveLoadingEnd]);

  React.useEffect(() => {
    if (prevIsRecursiveInitialLoading !== isRecursiveInitialLoading) {
      if (isRecursiveInitialLoading && onRecursiveLoadingStart) {
        onRecursiveLoadingStart();
      } else if (onRecursiveLoadingEnd) {
        onRecursiveLoadingEnd();
      }
    }
  }, [isRecursiveInitialLoading, prevIsRecursiveInitialLoading, onRecursiveLoadingEnd, onRecursiveLoadingStart]);

  // prevent empty message to be displayed before call BE to get the availabilities
  if (!isLoading && !!availabilities && isEmpty(availabilities)) {
    const message = showFilters
      ? 'messages.web.emptyAppointmentResultsFiltersAvailable'
      : 'messages.web.emptyAppointmentResultsFiltersNotAvailable';
    return (
      <Typography variant="h6">
        <MessageTemplate
          id={message}
          defaultMessage="0 appointment times found. Try adjusting your filters to view more results."
        />
      </Typography>
    );
  }
  return (
    <div>
      {(isLoading || !availabilities) && !isError && <ContentTableLoader isMobile={isMobile} />}
      {!isLoading && !isEmpty(availabilities) && (
        <ListAvailabilitiesGrouped availabilities={availabilities} isMobile={isMobile} onChange={onChange} {...rest} />
      )}
      {isFetchingNextPage && <ContentTableLoader isMobile={isMobile} />}
      {hasNextPage && !isLoading && !isFetchingNextPage && (
        <div className={classes.loadMoreWrapper}>
          <Button variant="contained" color="primary" fullWidth={isMobile} onClick={() => fetchNextPage()}>
            Load More
          </Button>
        </div>
      )}
    </div>
  );
};

export default withStyles(styles)(QueryListAvailabilitiesGrouped);
